import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  MessageCircle, Mail, Clock, CheckCircle, XCircle, Send, 
  Loader2, RefreshCw, Plus, Trash2, ExternalLink, Filter,
  AlertTriangle, User, Phone, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const MessageQueuePanel = () => {
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, ready: 0, sent: 0 });
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state for new message
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_mobile: '',
    customer_email: '',
    message_type: 'whatsapp',
    template_id: '',
    subject: '',
    message_body: '',
    scheduled_at: ''
  });

  useEffect(() => {
    fetchMessages();
    fetchTemplates();
  }, [fetchMessages, fetchTemplates]);

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await api.get(`/api/admin/message-queue?status=${status}&limit=100`);
      if (response.data.success) {
        setMessages(response.data.messages);
        setCounts(response.data.counts);
      }
    } catch (error) {
      toast.error('Failed to load message queue');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/templates');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      // Templates fetch failed silently
    }
  }, []);

  const handleMarkAsSent = async (messageId) => {
    try {
      await api.put(`/api/admin/message-queue/${messageId}/status`, { status: 'sent' });
      toast.success('Message marked as sent');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update message status');
    }
  };

  const handleDelete = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await api.delete(`/api/admin/message-queue/${messageId}`);
      toast.success('Message deleted');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleCreateMessage = async () => {
    if (!formData.customer_name || !formData.customer_mobile || !formData.message_body) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSaving(true);
    try {
      await api.post('/api/admin/message-queue', formData);
      toast.success('Message queued successfully');
      setShowCreateModal(false);
      resetForm();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to queue message');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_mobile: '',
      customer_email: '',
      message_type: 'whatsapp',
      template_id: '',
      subject: '',
      message_body: '',
      scheduled_at: ''
    });
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        template_id: templateId,
        subject: template.subject || '',
        message_body: template.body || ''
      }));
    }
  };

  const filteredTemplates = useMemo(() => 
    templates.filter(t => t.template_type === formData.message_type),
    [templates, formData.message_type]
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const formatScheduledTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return 'Ready to send';
    if (diff < 3600000) return `In ${Math.round(diff / 60000)} min`;
    if (diff < 86400000) return `In ${Math.round(diff / 3600000)} hours`;
    return date.toLocaleDateString();
  };

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="message-queue-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">WhatsApp Message Queue</h2>
          <p className="text-gray-500">Manage automated messages - click to send via WhatsApp Web</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMessages}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Queue Message
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('pending')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{counts.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('ready')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ready to Send</p>
                <p className="text-3xl font-bold text-blue-600">{counts.ready}</p>
              </div>
              <Send className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('sent')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sent</p>
                <p className="text-3xl font-bold text-green-600">{counts.sent}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <MessageCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-green-800">How it works</p>
              <p className="text-sm text-green-700">
                Messages are automatically generated by workflows. Click the WhatsApp button to open 
                WhatsApp Web with the pre-filled message. After sending, mark the message as "Sent" to track it.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs & Messages */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="ready">Ready ({counts.ready})</TabsTrigger>
          <TabsTrigger value="sent">Sent ({counts.sent})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {messages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No messages</h3>
                <p className="text-gray-500">
                  {activeTab === 'pending' && 'No pending messages in the queue'}
                  {activeTab === 'ready' && 'No messages ready to send'}
                  {activeTab === 'sent' && 'No sent messages yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <Card key={message.message_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Customer Info */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-full bg-gray-100">
                            <User className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">{message.customer_name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              {message.customer_mobile}
                            </p>
                          </div>
                          <Badge className={getStatusBadge(message.status)}>
                            {message.status}
                          </Badge>
                          <Badge variant="outline" className="flex items-center gap-1">
                            {message.message_type === 'whatsapp' ? (
                              <MessageCircle className="h-3 w-3" />
                            ) : (
                              <Mail className="h-3 w-3" />
                            )}
                            {message.message_type}
                          </Badge>
                        </div>

                        {/* Message Preview */}
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          {message.subject && (
                            <p className="font-medium text-sm mb-1">{message.subject}</p>
                          )}
                          <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                            {message.message_body}
                          </p>
                        </div>

                        {/* Meta Info */}
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Scheduled: {formatScheduledTime(message.scheduled_at)}
                          </span>
                          {message.workflow_id && (
                            <span>Workflow: {message.workflow_id}</span>
                          )}
                          {message.related_quote_id && (
                            <span>Quote: {message.related_quote_id}</span>
                          )}
                          {message.sent_at && (
                            <span className="text-green-600">
                              Sent: {new Date(message.sent_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {message.status !== 'sent' && message.whatsapp_link && (
                          <Button 
                            className="bg-green-600 hover:bg-green-700"
                            asChild
                          >
                            <a 
                              href={message.whatsapp_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />
                              Open WhatsApp
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          </Button>
                        )}
                        {message.status !== 'sent' && (
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkAsSent(message.message_id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Sent
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleDelete(message.message_id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Message Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Queue New Message</DialogTitle>
            <DialogDescription>
              Create a manual message to add to the queue
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Mobile Number *</Label>
                <Input
                  value={formData.customer_mobile}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_mobile: e.target.value }))}
                  placeholder="+27..."
                />
              </div>
            </div>

            <div>
              <Label>Message Type</Label>
              <Select 
                value={formData.message_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, message_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Use Template (Optional)</Label>
              <Select 
                value={formData.template_id} 
                onValueChange={applyTemplate}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {formData.message_type === 'email' && (
              <div>
                <Label>Subject</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject..."
                />
              </div>
            )}

            <div>
              <Label>Message *</Label>
              <Textarea
                value={formData.message_body}
                onChange={(e) => setFormData(prev => ({ ...prev, message_body: e.target.value }))}
                placeholder="Type your message..."
                rows={5}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use variables like {'{customer_name}'}, {'{quote_id}'} - they'll be replaced when sending
              </p>
            </div>

            <div>
              <Label>Schedule For (Optional)</Label>
              <Input
                type="datetime-local"
                value={formData.scheduled_at}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_at: e.target.value }))}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to queue immediately
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateMessage} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Queue Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessageQueuePanel;
